import type { MutableRefObject } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { ResizeObserver } from '@juggle/resize-observer';
import { useSettings } from '@wu/widget-core';

export function useItemsInRow(): [
  number,
  MutableRefObject<HTMLDivElement | null>,
  number
] {
  const { sectionsColumns, itemLayout, itemElements } = useSettings();
  const [itemsCount, setItemsCount] = useState(sectionsColumns);
  const ref = useRef<HTMLDivElement | null>(null);

  const columnBasis =
    (itemLayout === 1 && itemElements.includes('image') && 320) ||
    (itemLayout === 1 && !itemElements.includes('image') && 232) ||
    (itemLayout === 2 && itemElements.includes('image') && 340) ||
    (itemLayout === 2 && !itemElements.includes('image') && 204) ||
    (itemLayout === 3 && 200) ||
    320;

  const handleResize = useCallback(() => {
    if (!ref.current) {
      return;
    }

    const containerWidth = ref.current?.clientWidth ?? 0;

    if (Math.floor(containerWidth / sectionsColumns) < Number(columnBasis)) {
      const calculatedItemsCount = Math.floor(
        containerWidth / Number(columnBasis)
      );
      setItemsCount(Math.max(calculatedItemsCount, 1));
    } else {
      setItemsCount(sectionsColumns);
    }
  }, [ref.current, sectionsColumns, columnBasis]);

  useLayoutEffect(() => {
    if (!ref.current) {
      return;
    }

    handleResize();
    if (typeof ResizeObserver === 'function') {
      let resizeObserver: ResizeObserver | null = new ResizeObserver(() => {
        handleResize();
      });
      resizeObserver.observe(ref.current);

      return () => {
        if (!resizeObserver) {
          return;
        }

        resizeObserver.disconnect();
        resizeObserver = null;
      };
    }
    return () => null;
  }, [ref, handleResize]);

  return [itemsCount, ref, columnBasis];
}
